import { SubscriberStatus, useIM } from '@wui/im'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

import { TypesOfNotification } from '@/features/dont-disturb-notifications'

import { useStoreContacts } from '@/entities/contact'
import { usePreferencesStore, PreferenceKey } from '@/entities/preference'

import { OwnStatus } from '@/shared/types/store'
import { compare } from '@/shared/utils'

const settingsNotificationsDefault = {
  isUseSoundNotifications: false,
  isDndAutoActivate: false,
  isHiddenNotifications: false,
  dontDisturbGroups: [] as TypesOfNotification[],
  callStatusDoNotDisturb: {
    enabled: false,
    oldStatusText: '',
    newStatusText: '',
    oldStatusNumber: 0,
    newStatusNumber: 3,
    isChangedByCall: false,
  },
}

export const useStoreSettingsNotifications = defineStore('notification-settings', () => {
  const preferencesStore = usePreferencesStore()
  const { preferences } = storeToRefs(preferencesStore)
  const { setPreferences } = preferencesStore

  const isUseSoundNotifications = computed({
    get: () => preferences.value[PreferenceKey.USE_SOUND_NOTIFICATION] as boolean ?? settingsNotificationsDefault.isUseSoundNotifications,
    set: (value: boolean) => {
      setPreferences(PreferenceKey.USE_SOUND_NOTIFICATION, value)
    },
  })

  const isHiddenNotifications = computed({
    get: () => preferences.value[PreferenceKey.IS_HIDDEN_NOTIFICATIONS] as boolean ?? settingsNotificationsDefault.isHiddenNotifications,
    set: (value: boolean) => {
      setPreferences(PreferenceKey.IS_HIDDEN_NOTIFICATIONS, value)
    },
  })

  const isDndAutoActivate = computed({
    get: () => preferences.value[PreferenceKey.DND_AUTO_ACTIVATE] as boolean ?? settingsNotificationsDefault.isDndAutoActivate,
    set: (value: boolean) => {
      setPreferences(PreferenceKey.DND_AUTO_ACTIVATE, value)
    },
  })

  const dontDisturbGroups = computed({
    get: () =>
      (preferences.value[PreferenceKey.DONT_DITURB_GROUPS] as TypesOfNotification[] | undefined) ??
      [...settingsNotificationsDefault.dontDisturbGroups],
    set: (value: TypesOfNotification[]) => {
      setPreferences(PreferenceKey.DONT_DITURB_GROUPS, value)
    },
  })

  const isInDontDisturbGroups = (group: TypesOfNotification) => {
    return dontDisturbGroups.value?.includes(group)
  }

  const callStatusDoNotDisturb = ref({ ...settingsNotificationsDefault.callStatusDoNotDisturb })

  // Сделал debounce чтобы вотчеры не триггерили друг друга
  let debounceTimer: NodeJS.Timeout | null = null
  watch(() => preferences.value[PreferenceKey.CALL_STATUS_DO_NOT_DISTURB], (settingsNew, settingsOld) => {
    if (JSON.stringify(settingsNew) === JSON.stringify(settingsOld)) {
      return
    }
    callStatusDoNotDisturb.value = {
      ...settingsNotificationsDefault.callStatusDoNotDisturb,
      ...callStatusDoNotDisturb.value,
      ...settingsNew as typeof settingsNotificationsDefault.callStatusDoNotDisturb,
    }
  })
  watch(callStatusDoNotDisturb, () => {
    if (compare(callStatusDoNotDisturb.value, preferences.value[PreferenceKey.CALL_STATUS_DO_NOT_DISTURB])) {
      return
    }
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }
    debounceTimer = setTimeout(() => {
      setPreferences(PreferenceKey.CALL_STATUS_DO_NOT_DISTURB, callStatusDoNotDisturb.value)
    }, 800)
  }, { deep: true })

  const updateOldStatuses = (oldStatusNumber: number, oldStatusText: string) => {
    callStatusDoNotDisturb.value = {
      ...callStatusDoNotDisturb.value,
      oldStatusNumber,
      oldStatusText,
    }
  }

  const setCallStatusDoNotDisturbIsChangedByCall = (isChangedByCall: boolean) => {
    callStatusDoNotDisturb.value = {
      ...callStatusDoNotDisturb.value,
      isChangedByCall,
    }
  }

  const applyCallStatusDoNotDisturb = (): Promise<boolean> => {
    const { ownStatus, ownStatusMessage } = useStoreContacts()
    if (ownStatus !== OwnStatus.ONLINE && ownStatus !== OwnStatus.ABSENT) {
      return Promise.resolve(false)
    }

    updateOldStatuses(ownStatus, ownStatusMessage)
    const { setSubscriberStatus } = useIM()
    return setSubscriberStatus({
      status: SubscriberStatus.DO_NOT_DISTURB,
      text: callStatusDoNotDisturb.value.newStatusText,
    })
      .then((isSuccess) => {
        if (isSuccess) {
          setCallStatusDoNotDisturbIsChangedByCall(true)
        }
        return !!isSuccess
      })
      .catch((e: Error) => {
        console.error(e)
        return false
      })
  }

  const resetCallStatusIfChangedByCall = (): Promise<boolean> => {
    if (!callStatusDoNotDisturb.value.isChangedByCall) {
      return Promise.resolve(false)
    }

    const { setSubscriberStatus } = useIM()
    return setSubscriberStatus({
      status: callStatusDoNotDisturb.value.oldStatusNumber,
      text: callStatusDoNotDisturb.value.oldStatusText,
    })
      .then((isSuccess) => {
        if (isSuccess) {
          setCallStatusDoNotDisturbIsChangedByCall(false)
        }
        return !!isSuccess
      })
      .catch((e: Error) => {
        console.error(e)
        return false
      })
  }

  return {
    isUseSoundNotifications,
    isHiddenNotifications,
    isDndAutoActivate,
    callStatusDoNotDisturb,
    dontDisturbGroups,
    isInDontDisturbGroups,
    updateOldStatuses,
    setCallStatusDoNotDisturbIsChangedByCall,
    applyCallStatusDoNotDisturb,
    resetCallStatusIfChangedByCall,
  }
})
